import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import frFR from "antd/es/locale/fr_FR";
import enUS from "antd/es/locale/en_US";

// Returns the antd locale matching the current i18n language
// and keeps dayjs in sync (month/day names, etc.)
const ANTD_LOCALES = { fr: frFR, en: enUS };

export const useAntdLocale = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";

  useEffect(() => {
    dayjs.locale(lang === "fr" ? "fr" : "en");
  }, [lang]);

  return ANTD_LOCALES[lang] || enUS;
};

// DST-aware Paris UTC offset ("UTC+1" or "UTC+2") — used for the API's `utc` param
export const getCurrentTimeZone = () => {
  const now = new Date();

  const parisOffset = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Paris",
    timeZoneName: "long",
  })
    .formatToParts(now)
    .find((part) => part.type === "timeZoneName").value;

  const isCEST =
    parisOffset.includes("Central European Summer Time") ||
    parisOffset.includes("GMT+2") ||
    parisOffset.includes("UTC+2");

  return isCEST ? "UTC+2" : "UTC+1";
};

export const getCurrentFranceHour = () => {
  const now = new Date();
  return parseInt(
    now.toLocaleTimeString("en-US", {
      timeZone: "Europe/Paris",
      hour12: false,
      hour: "2-digit",
    }),
    10,
  );
};

export const getCurrentFranceDateObject = () => {
  const now = new Date();
  const franceDate = new Date(
    now.toLocaleString("en-US", { timeZone: "Europe/Paris" }),
  );

  return {
    day: franceDate.getDate().toString().padStart(2, "0"),
    month: (franceDate.getMonth() + 1).toString().padStart(2, "0"),
    year: franceDate.getFullYear().toString(),
  };
};

export const getLocalNowMinusOneHour = () => {
  const now = new Date();
  now.setHours(now.getHours() - 1);

  const pad = (n) => String(n).padStart(2, "0");

  return {
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
};
export const formatDateValue = (value) => {
  if (!value) return "--";
  const cleaned = value.replace("Z", ""); // handles UTC-suffixed timestamps too
  const [datePart, timePart] = cleaned.split("T");
  if (!datePart) return "--";

  const [year, month, day] = datePart.split("-");
  const formattedDate = `${day}-${month}-${year}`;

  if (!timePart) return formattedDate;

  const [hours, minutes] = timePart.split(":");
  if (hours === "00" && minutes === "00") return formattedDate;

  return `${formattedDate} ${hours}:${minutes}`;
};